import { RadioGroupNew, RadioButtonNew, NumberField, Callout, StandardDialog, ExternalLink } from "@octopusdeploy/design-system-components";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { type Repository, Permission, type OpenTelemetryTraceFileExportConfigurationResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useState } from "react";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import { ExpandableFormSection, Note, Summary } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import DateFormatter from "~/utils/DateFormatter";
const MIN_STORAGE_SIZE_MB = 250;
const STORAGE_WARNING_THRESHOLD_PERCENT = 80;
interface OpenTelemetryTraceFileExportConfigurationSectionProps {
    model: OpenTelemetryTraceFileExportConfigurationResource;
    setModel: (model: OpenTelemetryTraceFileExportConfigurationResource) => void;
    getFieldError: (fieldName: string) => string | undefined;
}
export interface ServerTracesPageProps {
    loaderData: ServerTracesPageLoaderData;
}
export interface ServerTracesPageLoaderData {
    openTelemetryTraceFileExportConfiguration: OpenTelemetryTraceFileExportConfigurationResource;
}
export async function ServerTracesPageLoader(repository: Repository): Promise<ServerTracesPageLoaderData> {
    return {
        openTelemetryTraceFileExportConfiguration: await repository.OpenTelemetryTraceFileExportConfiguration.get(),
    };
}
export const PageTitle = "Server Traces Configuration";
export function ServerTracesPage({ loaderData }: ServerTracesPageProps) {
    const { model, setModel, formProps, getFieldError, createSaveAction, isDisabled: formDisabledState, } = useForm({
        formName: "Edit Server Otel Export Configuration",
        initialModel: loaderData.openTelemetryTraceFileExportConfiguration,
        validate: (model) => {
            const fieldErrors: Record<string, string> = {};
            if (model.RetentionDays < 0) {
                fieldErrors.RetentionDays = "Retention period cannot be negative";
            }
            if (model.MaxStorageSizeMegabytes < MIN_STORAGE_SIZE_MB) {
                fieldErrors.MaxStorageSizeMegabytes = `Max storage size must be at least ${MIN_STORAGE_SIZE_MB} MB`;
            }
            const errorMessages = Object.values(fieldErrors);
            if (errorMessages.length > 0) {
                return { errorMessage: errorMessages.join(". "), fieldErrors };
            }
            return undefined;
        },
        onSubmit: (repository, model) => {
            return repository.OpenTelemetryTraceFileExportConfiguration.modify(model);
        },
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: { permission: Permission.AdministerSystem },
        snackbarSaveText: "OpenTelemetry trace file export configuration updated",
    });
    const { result: metadata, refetch: refetchMetadata } = useQuery((repository) => repository.OpenTelemetryTraceFileExportConfiguration.getMetadata(), [], "Load trace file metadata");
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const { execute: deleteAllTraceFiles, isExecuting: isDeletingTraceFiles } = useMutation({
        name: "Delete all trace files",
        action: (repository) => repository.OpenTelemetryTraceFileExportConfiguration.deleteAllTraceFiles(),
        afterComplete: async () => refetchMetadata(),
    });
    const currentStorageMB = parseFloat(((metadata?.SizeInBytes || 0) / (1024 * 1024)).toFixed(2));
    const renderStatusSection = (storagePercentage: number) => {
        if (!metadata || metadata.SizeInBytes === 0 || model.MaxStorageSizeMegabytes <= 0) {
            return null;
        }
        const isOverStorageLimit = currentStorageMB > model.MaxStorageSizeMegabytes;
        const isNearStorageLimit = storagePercentage >= STORAGE_WARNING_THRESHOLD_PERCENT;
        const hasRetentionPeriod = model.RetentionDays > 0;
        if (isOverStorageLimit) {
            return (<Callout type="danger" title="Storage limit exceeded">
                    Current storage usage ({currentStorageMB} MB) exceeds the configured maximum ({model.MaxStorageSizeMegabytes} MB).
                </Callout>);
        }
        if (isNearStorageLimit && hasRetentionPeriod) {
            return (<Callout type="warning" title="Storage approaching limit">
                    Storage is at {storagePercentage}%. When the {model.MaxStorageSizeMegabytes} MB limit is reached, trace files within the {model.RetentionDays} day retention period will be deleted early to make room for new traces. Consider
                    increasing max storage size.
                </Callout>);
        }
        return null;
    };
    const storagePercentage = model.MaxStorageSizeMegabytes > 0 ? Math.round((currentStorageMB / model.MaxStorageSizeMegabytes) * 100) : 0;
    const hasTraceData = metadata && metadata.SizeInBytes > 0;
    const isFormDirty = !formDisabledState.value;
    const pageActions: PageAction[] = [
        {
            type: "button",
            label: "Clear trace data",
            buttonType: "destructive",
            hasPermissions: isAllowed({ permission: Permission.AdministerSystem }),
            disabled: !hasTraceData || model.Enabled || isFormDirty,
            extraContext: !isFormDirty && model.Enabled ? "Disable trace capture before clearing trace data" : undefined,
            onClick: () => setIsDeleteDialogOpen(true),
        },
    ];
    return (<PageContent header={{ title: PageTitle, breadcrumbs: [{ label: "Diagnostics", pageUrl: links.diagnosticsPage.generateUrl() }], primaryAction: createSaveAction(), pageActions }}>
            <StandardDialog title="Clear trace data?" open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} width="small" showDismissButton={false} primaryButton={{
            label: "Clear trace data",
            importance: "destructive",
            disabled: isDeletingTraceFiles,
            onClick: async () => {
                await deleteAllTraceFiles();
                setIsDeleteDialogOpen(false);
            },
        }} secondaryButton={{
            label: "Cancel",
            importance: "quiet",
            onClick: () => setIsDeleteDialogOpen(false),
        }}>
                <p>This will permanently delete all trace files across all server nodes.</p>
                <p>This action cannot be undone.</p>
            </StandardDialog>
            <PermissionCheck permission={[Permission.AdministerSystem]}>
                <Section>
                    Configure how Octopus Server nodes will record and store OpenTelemetry traces on the filesystem. <ExternalLink href="OpenTelemetryTraceFiles">Learn more</ExternalLink>
                </Section>
                <Section>
                    {renderStatusSection(storagePercentage)}
                    <p>
                        <strong>Current Storage:</strong> {currentStorageMB} MB
                        {model.MaxStorageSizeMegabytes > 0 && ` of ${model.MaxStorageSizeMegabytes} MB configured (${storagePercentage}%)`}
                    </p>
                    {metadata?.OldestFileTimestamp && (<p>
                            <strong>Oldest Trace File:</strong> Created {DateFormatter.dateToShortFormat(metadata.OldestFileTimestamp)} ({DateFormatter.momentAgo(metadata.OldestFileTimestamp)})
                        </p>)}
                </Section>

                <Form {...formProps}>
                    <OpenTelemetryTraceFileExportConfigurationSection model={model} setModel={setModel} getFieldError={getFieldError}/>
                </Form>
            </PermissionCheck>
        </PageContent>);
}
function OpenTelemetryTraceFileExportConfigurationSection({ model, setModel, getFieldError }: OpenTelemetryTraceFileExportConfigurationSectionProps) {
    return (<>
            <ExpandableFormSection errorKey="IsEnabled" title="Capture Server Traces" summary={model.Enabled ? Summary.summary("Enabled - Traces are being captured") : Summary.default("Disabled - No traces are being captured")} help="Capture and store server traces to the local file system">
                <RadioGroupNew value={!!model.Enabled} onChange={(value) => setModel({ ...model, Enabled: value })} label="">
                    <RadioButtonNew name="enabled" value={true} label="Enabled"/>
                    <RadioButtonNew name="disabled" value={false} label="Disabled"/>
                </RadioGroupNew>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="RetentionDays" title="Retention Period" summary={model.RetentionDays > 0 ? Summary.summary(`${model.RetentionDays} ${model.RetentionDays === 1 ? "day" : "days"}`) : Summary.summary("No retention period - files are only deleted when storage limit is reached")} help="Number of days to retain trace files before deletion">
                <NumberField value={model.RetentionDays} onChange={(x) => setModel({ ...model, RetentionDays: x ?? 0 })} label="Retention period" placeholder="0 (unlimited)" suffix={model.RetentionDays === 1 ? "day" : "days"} min={0} error={getFieldError("RetentionDays")}/>
                <Note>
                    Files older than this period are deleted even if storage space remains available. Set to 0 for unlimited retention (files only deleted when storage limit is reached). Note: If storage fills up, files may be deleted early even if
                    within the retention period.
                </Note>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="MaxStorageSizeMegabytes" title="Max Storage Size" summary={model.MaxStorageSizeMegabytes > 0 ? Summary.summary(`${model.MaxStorageSizeMegabytes.toLocaleString()} MB`) : Summary.placeholder(`No maximum storage size specified`)} help={`Maximum disk space for trace files (minimum ${MIN_STORAGE_SIZE_MB} MB required)`}>
                <NumberField value={model.MaxStorageSizeMegabytes} onChange={(x) => setModel({ ...model, MaxStorageSizeMegabytes: x ?? 0 })} label="Max storage size" placeholder={`Minimum ${MIN_STORAGE_SIZE_MB} MB`} suffix="MB" min={MIN_STORAGE_SIZE_MB} error={getFieldError("MaxStorageSizeMegabytes")}/>
                <Note>When this limit is reached, oldest trace files are deleted regardless of age. Minimum requirement: {MIN_STORAGE_SIZE_MB} MB.</Note>
            </ExpandableFormSection>
        </>);
}
